import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { supabase } from '@/config/supabase'
import { formatDate } from '@/lib/formatters'
import { 
    Activity, ArrowUpRight, ArrowDownLeft, Search, 
    Clock, CheckCircle, XCircle, ChevronRight, Eye, RefreshCw, X, Handshake
} from 'lucide-react'

export function IntegracaoLogsPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [tab, setTab] = useState<'asaas' | 'parceiros' | 'notificacoes'>('asaas')
    const [logs, setLogs] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedLog, setSelectedLog] = useState<any | null>(null)

    useEffect(() => { setPageTitle('Logs de Integração') }, [setPageTitle])

    useEffect(() => {
        fetchLogs()
    }, [tab])

    async function fetchLogs() {
        setLoading(true)
        try {
            let query: any
            
            if (tab === 'notificacoes') {
                query = supabase
                    .schema('veicular_system')
                    .from('veic_parceiro_webhook_logs')
                    .select('*, contrato:veic_contratos(external_reference), parceiro:veic_parceiros(nome)')
            } else {
                query = supabase
                    .schema('veicular_system')
                    .from('veic_webhook_logs')
                    .select('*, parceiro:veic_parceiros(nome)')
                    .eq('origem', tab === 'asaas' ? 'asaas' : 'api-parceiro')
            }

            const { data, error } = await query
                .order('created_at', { ascending: false })
                .limit(50)

            if (error) throw error
            setLogs(data || [])
        } catch (err) {
            console.error('Erro ao buscar logs:', err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Histórico de Integração</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Monitoramento de requisições API e Webhooks</p>
                </div>
                <button onClick={fetchLogs} className="btn btn-secondary btn-sm gap-2">
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
                </button>
            </div>

            {/* Tabs */}
            <div className="flex bg-surface-100 p-1 rounded-xl w-fit">
                <button 
                    onClick={() => setTab('asaas')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${tab === 'asaas' ? 'bg-white text-primary-600 shadow-sm' : 'text-surface-700/60 hover:text-surface-900'}`}
                >
                    <ArrowDownLeft size={16} /> Entradas Asaas
                </button>
                <button 
                    onClick={() => setTab('parceiros')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${tab === 'parceiros' ? 'bg-white text-primary-600 shadow-sm' : 'text-surface-700/60 hover:text-surface-900'}`}
                >
                    <Handshake size={16} /> Entradas Parceiros
                </button>
                <button 
                    onClick={() => setTab('notificacoes')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${tab === 'notificacoes' ? 'bg-white text-primary-600 shadow-sm' : 'text-surface-700/60 hover:text-surface-900'}`}
                >
                    <ArrowUpRight size={16} /> Para Parceiros
                </button>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="table min-w-full">
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>{tab === 'notificacoes' ? 'Parceiro / Contrato' : 'Origem / Evento'}</th>
                                <th>Status</th>
                                <th className="text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="py-20 text-center">
                                        <RefreshCw size={24} className="animate-spin text-primary-600 mx-auto" />
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="py-20 text-center">
                                        <Activity size={40} className="text-surface-200 mx-auto mb-2" />
                                        <p className="text-surface-700/40">Nenhum log encontrado</p>
                                    </td>
                                </tr>
                            ) : logs.map((log) => (
                                <tr key={log.id} className="hover:bg-surface-50 transition-colors">
                                    <td className="whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <Clock size={14} className="text-surface-400" />
                                            <span className="text-sm text-surface-700">{formatDate(log.created_at)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-surface-900">
                                                {tab === 'notificacoes' 
                                                    ? (log.parceiro?.nome || 'Parceiro')
                                                    : (log.parceiro?.nome || 'Asaas')
                                                }
                                            </span>
                                            <span className="text-xs text-surface-700/50">
                                                {tab === 'notificacoes' 
                                                    ? (log.contrato?.external_reference || log.evento)
                                                    : log.evento
                                                }
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1.5">
                                            {log.status === 'processado' || log.status === 'enviado' ? (
                                                <CheckCircle size={14} className="text-success-500" />
                                            ) : (
                                                <XCircle size={14} className="text-danger-500" />
                                            )}
                                            <span className={`text-xs font-bold uppercase ${
                                                log.status === 'processado' || log.status === 'enviado' ? 'text-success-700' : 'text-danger-700'
                                            }`}>
                                                {log.status}
                                                {log.response_status ? ` (${log.response_status})` : ''}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="text-right">
                                        <button 
                                            onClick={() => setSelectedLog(log)}
                                            className="p-2 rounded-lg hover:bg-surface-200 text-surface-700 transition-colors"
                                        >
                                            <Eye size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Detalhes */}
            {selectedLog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="glass-card w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                            <h3 className="font-bold text-surface-900">Detalhes da Requisição</h3>
                            <button onClick={() => setSelectedLog(null)} className="p-2 rounded-lg hover:bg-surface-100 text-surface-500">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[10px] font-bold text-surface-700/40 uppercase">ID</p>
                                    <p className="text-xs font-mono">{selectedLog.id}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-surface-700/40 uppercase">Timestamp</p>
                                    <p className="text-xs">{formatDate(selectedLog.created_at)}</p>
                                </div>
                            </div>

                            {selectedLog.webhook_url && (
                                <div>
                                    <p className="text-[10px] font-bold text-surface-700/40 uppercase mb-1">URL de Destino</p>
                                    <div className="p-2 bg-surface-100 rounded text-xs font-mono break-all">
                                        {selectedLog.webhook_url}
                                    </div>
                                </div>
                            )}

                            <div>
                                <p className="text-[10px] font-bold text-surface-700/40 uppercase mb-1">Payload (JSON)</p>
                                <pre className="p-4 bg-surface-900 text-surface-100 rounded-xl text-[11px] font-mono overflow-x-auto">
                                    {JSON.stringify(selectedLog.payload, null, 2)}
                                </pre>
                            </div>

                            {(selectedLog.response_body || selectedLog.erro) && (
                                <div>
                                    <p className="text-[10px] font-bold text-surface-700/40 uppercase mb-1">Resposta / Erro</p>
                                    <pre className="p-4 bg-danger-50 text-danger-900 border border-danger-100 rounded-xl text-[11px] font-mono overflow-x-auto">
                                        {selectedLog.response_body || selectedLog.erro}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
